<template>
    <div class="main-layout__container auction-create">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку аукционов'"
                    :to="router.resolve({ name: 'AuctionsList' }).href"
                />
            </div>

            <div class="content-header__title">Создание аукциона</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <div class="content__body">
            <div class="form__content">
                <div class="form__fields">
                    <div class="form__field-group form__field-group--by-2">
                        <UIDatePicker
                            v-model="form.start_date"
                            label="Дата начала"
                            :options="{ enableTime: true }"
                            :error-message="errors?.start_date?.join() ?? ''"
                        />

                        <UIDatePicker
                            v-model="form.end_date"
                            label="Дата окончания"
                            :options="{ enableTime: true }"
                            :error-message="errors?.end_date?.join() ?? ''"
                        />
                    </div>
                </div>
            </div>

            <div v-if="selectedCars.length === 0" class="add-car">
                <div class="add-car__icon">
                    <img v-svg-inline src="@/assets/icons/auction/auto.svg" />
                </div>

                <div class="add-car__label">
                    Нажмите кнопку «Добавить авто», чтобы выбрать автомобиль
                </div>

                <UISimpleButton
                    class="add-car__button"
                    @clicked="openAddCarToAuctionModal"
                >
                    Добавить авто
                </UISimpleButton>
            </div>
            <div v-else class="cars-list">
                <CarListItem
                    v-for="(car, index) in selectedCars"
                    :key="'car-' + car.id"
                    :car="car"
                    :errors="errors?.cars && errors.cars.length > index ? (errors.cars[index].start_price ?? []) : []"
                    @removed="onRemoveCar"
                    @changed="onChangedCar"
                />

                <div class="add-car">
                    <UISimpleButton
                        class="add-car__button"
                        button-type="outlined"
                        @clicked="openAddCarToAuctionModal"
                    >
                        <template #icon>
                            <img v-svg-inline src="@/assets/icons/ui/plus.svg" />
                        </template>
                        Добавить еще авто
                    </UISimpleButton>
                </div>
            </div>
        </div>

        <div v-if="selectedCars.length > 0" class="content__footer">
            <div class="form__actions">
                <UISimpleButton
                    button-type="outlined"
                    :loading="saveLoading"
                    @clicked="saveAsDraft"
                >
                    Сохранить черновик
                </UISimpleButton>

                <UISimpleButton
                    :loading="saveLoading"
                    @clicked="saveAsPublished"
                >
                    Создать аукцион
                </UISimpleButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { inject, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';

    import { AuctionCreateForm, AuctionCreateFormErrors } from '@/types/auction.type.ts';
    import { Car } from '@/types/car/car.type.ts';

    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUnsavedDataWarning from '@/plugins/useUnsavedDataWarning.ts';

    import { ModalPlugin } from '@/plugins/modalPlugin';
    import { formatToUTC } from '@/utils/DateTimeFormatter.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import AuctionConfirmPublish from '@/components/auction/AuctionConfirmPublish/AuctionConfirmPublish.vue';

    import AddCarToAuctionModal from '@/modals/AddCarToAuctionModal/AddCarToAuctionModal.vue';

    import CarListItem from '@/views/auctions/CreateAuction/components/CarListItem/CarListItem.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const router = useRouter();
    const { createAuction } = useAuctionRepository();
    const { showSuccess } = useNotifications();
    const { setDirty } = useUnsavedDataWarning();

    const selectedCars = ref<Car[]>([]);
    const form = ref<AuctionCreateForm>({
        start_date: '',
        end_date: '',
        cars: [],
    });
    const errors = ref<AuctionCreateFormErrors | null>(null);
    const saveLoading = ref<boolean>(false);

    watch(form, () => {
        setDirty(true);
    }, { deep: true });

    watch(selectedCars, () => {
        fillForm();
    }, { deep: true });

    function openAddCarToAuctionModal(): void {
        $modal.open({
            component: AddCarToAuctionModal,
            props: {
                initialCars: selectedCars,
            },
        });
    }

    function onRemoveCar(car: Car): void {
        const carIndex = selectedCars.value.findIndex((selectedCar: Car) => selectedCar.id === car.id);

        if (carIndex >= 0) {
            selectedCars.value.splice(carIndex, 1);
        }
    }

    function onChangedCar(car: Car): void {
        const carIndex = selectedCars.value.findIndex((selectedCar: Car) => selectedCar.id === car.id);

        if (carIndex >= 0) {
            selectedCars.value[carIndex] = car;
        }
    }

    function saveAsDraft(): void {
        form.value.is_published = 0;

        saveAuction().then(() => {
            showSuccess({
                title: 'Аукцион создан',
            });

            router.push({ name: 'AuctionsList' });
        });
    }

    function saveAsPublished(): void {
        const requestForm = mapAuctionUpdateFormToRequestForm();

        $modal.open({
            component: AuctionConfirmPublish,
            modalType: 'narrow',
            props: {
                startDate: requestForm.start_date,
                endDate: requestForm.end_date,
                cars: selectedCars,
                onConfirm: async () => {
                    form.value.is_published = 1;

                    // noinspection EmptyCatchBlockJS
                    try {
                        await saveAuction();

                        showSuccess({
                            title: 'Аукцион опубликован',
                        });

                        router.push({ name: 'AuctionsList' });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    catch (e) {}
                },
            },
        });
    }

    function saveAuction(): Promise<void> {
        saveLoading.value = true;

        return new Promise((resolve, reject) => {
            createAuction(mapAuctionUpdateFormToRequestForm())
                .then(() => {
                    setDirty(false);
                    resolve();
                })
                .catch((createErrors: AuctionCreateFormErrors) => {
                    errors.value = createErrors;
                    reject();
                })
                .finally(() => {
                    saveLoading.value = false;
                });
        });
    }

    function mapAuctionUpdateFormToRequestForm(): AuctionCreateForm {
        return {
            start_date: form.value.start_date.length > 0
                ? formatToUTC(form.value.start_date, { fromFormat: 'dd.LL.yyyy HH:mm' })
                : '',
            end_date: form.value.end_date.length > 0?
                formatToUTC(form.value.end_date, { fromFormat: 'dd.LL.yyyy HH:mm' })
                : '',
            cars: [...form.value.cars],
            is_published: form.value.is_published,
        };
    }

    function fillForm(): void {
        form.value.cars = selectedCars.value.map((car: Car) => ({
            id: car.id,
            start_price: car.start_price,
        }));
    }
</script>

<style scoped lang="scss">
    @use './CreateAuction.scss' as *;
</style>
